import React, { useState } from 'react';
import { ChevronDown, HelpCircle, MessageSquare, ArrowRight } from 'lucide-react';
import { SITE_CONTACT } from '../data/siteData';

export interface FaqItem {
  question: string;
  answer: string;
}

interface PageFaqSectionProps {
  title?: string;
  subtitle?: string;
  categoryName: string;
  faqs: FaqItem[];
  onOpenBooking?: () => void;
}

export const PageFaqSection: React.FC<PageFaqSectionProps> = ({
  title = 'Frequently Asked Questions',
  subtitle,
  categoryName,
  faqs,
  onOpenBooking,
}) => {
  const [openIndices, setOpenIndices] = useState<number[]>([0]);

  const toggleIndex = (idx: number) => {
    setOpenIndices((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };

  return (
    <section className="pt-16 pb-12 border-t border-slate-200 text-left">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-3.5 py-1 text-xs font-semibold text-[#855b14] font-mono mb-3">
            <HelpCircle className="h-3.5 w-3.5 text-[#855b14]" />
            <span>{categoryName} Technical &amp; Commercial FAQ</span>
          </div>

          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 font-serif-heading tracking-tight">
            {title}
          </h2>

          <p className="mt-3 text-base text-slate-700 leading-relaxed font-sans">
            {subtitle ||
              `Clear answers regarding ${categoryName} pricing in KES, delivery turnaround times, Lipa na M-Pesa checkouts, and production SLAs across Kenya.`}
          </p>
        </div>

        {/* Accordion List */}
        <div className="space-y-3" role="region" aria-label={`${categoryName} Questions and Answers`}>
          {faqs.map((faq, idx) => {
            const isOpen = openIndices.includes(idx);
            return (
              <div
                key={idx}
                className={`rounded-2xl border transition-all duration-200 overflow-hidden ${
                  isOpen
                    ? 'border-slate-300 bg-white shadow-xs'
                    : 'border-slate-200 bg-slate-50/70 hover:bg-white hover:border-slate-300'
                }`}
              >
                <button
                  type="button"
                  onClick={() => toggleIndex(idx)}
                  className="w-full p-5 text-left flex items-center justify-between gap-4 cursor-pointer focus:outline-hidden min-h-[48px]"
                  aria-expanded={isOpen}
                >
                  <span className="text-base font-bold text-slate-900 leading-snug">
                    {faq.question}
                  </span>
                  <div
                    className={`h-7 w-7 rounded-full flex items-center justify-center shrink-0 transition-transform duration-200 ${
                      isOpen ? 'bg-[#c59b4c] text-slate-950 rotate-180' : 'bg-slate-200 text-slate-700'
                    }`}
                  >
                    <ChevronDown className="h-4 w-4" />
                  </div>
                </button>

                {isOpen && (
                  <div className="px-5 pb-5 text-sm text-slate-700 leading-relaxed border-t border-slate-100 pt-3.5 font-sans space-y-2">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Direct WhatsApp / Consultation Callout */}
        <div className="rounded-2xl bg-[#080d11] p-6 text-white border border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <div className="text-xs font-mono font-bold uppercase tracking-wider text-[#fcd34d] mb-1">
              Have a specific question not covered here?
            </div>
            <p className="text-xs text-slate-300">
              Speak directly with our senior technology desk in Nairobi. Guaranteed response within 2 hours.
            </p>
          </div>

          <div className="flex items-center gap-2.5 shrink-0 w-full sm:w-auto">
            {onOpenBooking && (
              <button
                onClick={onOpenBooking}
                className="flex-1 sm:flex-none px-4 py-2.5 rounded-xl bg-[#c59b4c] hover:bg-[#b08535] text-slate-950 font-bold text-xs flex items-center justify-center gap-1.5 transition-colors cursor-pointer min-h-[44px]"
              >
                <span>Book 45-Min Session</span>
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            )}

            <a
              href={SITE_CONTACT.whatsappUrl}
              target="_blank"
              rel="noreferrer"
              className="flex-1 sm:flex-none px-4 py-2.5 rounded-xl border border-slate-700 bg-slate-900 hover:bg-slate-800 text-white font-semibold text-xs flex items-center justify-center gap-1.5 transition-colors cursor-pointer min-h-[44px]"
            >
              <MessageSquare className="h-3.5 w-3.5 text-emerald-400" />
              <span>WhatsApp Chat</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
